import type { Filter } from '@spendesk/bookkeeping-core-types';

import { toCoreTypeFilter } from 'modules/payable/hooks/api/payable-filter/payableFilterTransformer';
import type { Filter as DomainFilter } from 'src/core/common/components/FilterBuilder';

import type { BucketType } from './buckets';

export type GroupFilter =
  | 'expenseType'
  | 'supplier'
  | 'expenseAccount'
  | 'taxAccount'
  | 'receiptStatus';

export type SortFilter = 'date-asc' | 'date-desc';

export const defaultSortFilter: SortFilter = 'date-asc';
export const defaultGroupFilter: GroupFilter = 'receiptStatus';

export type Filters = {
  sort: SortFilter;
  group?: GroupFilter;
  search?: string;
};

export const toAPIPayableFiltersV2 = ({
  filters,
  bucketType,
  groupFilter,
  groupId,
  withBookkeepingStartDate,
}: {
  filters: DomainFilter | undefined;
  bucketType?: BucketType;
  groupFilter?: GroupFilter | undefined;
  groupId?: string | null;
  withBookkeepingStartDate?: boolean;
}): Filter | undefined => {
  const subfilters = filters ? [toCoreTypeFilter(filters)] : [];

  subfilters.push({
    field: 'bookkeepingStatus',
    operator: '=',
    value: ['toPrepare'],
  });

  // Proof filter
  switch (bucketType) {
    case 'withoutProof':
      subfilters.push({
        field: 'documentaryEvidenceStatus',
        operator: '=',
        value: ['missing', 'declaredAsInvalid'],
      });
      break;
    case 'withProof':
      subfilters.push({
        field: 'documentaryEvidenceStatus',
        operator: '=',
        value: ['provided'],
      });
      break;
    case 'missingProof':
      subfilters.push({
        field: 'documentaryEvidenceStatus',
        operator: '=',
        value: ['cannotBeProvided'],
      });
      break;
    default:
      break;
  }

  // Grouping filter
  if (groupFilter && groupId) {
    if (groupFilter === 'expenseType') {
      subfilters.push({
        field: 'payableType',
        operator: '=',
        value: (() => {
          if (groupId === 'cardPurchase') {
            return [
              'singleCardPurchase',
              'physicalCardPurchase',
              'multiUseCardPurchase',
              'subscription',
            ];
          }

          if (groupId === 'cardReversal') {
            return ['cardRefund'];
          }

          if (groupId === 'claimedBill') {
            return ['expenseClaim'];
          }

          if (groupId === 'mileageExpense') {
            return ['mileageAllowance'];
          }

          if (groupId === 'perDiemExpense') {
            return ['perDiemAllowance'];
          }

          if (groupId === 'creditNote' || groupId === 'invoicePurchase') {
            return [groupId];
          }

          return null;
        })(),
      });
    }

    if (groupFilter === 'supplier') {
      subfilters.push({
        field: 'supplier',
        operator: '=',
        value: [groupId],
      });
    }
  }

  if (withBookkeepingStartDate === true) {
    subfilters.push({
      field: 'withBookkeepingStartDate',
      operator: '=',
      value: true,
    });
  }

  return {
    operator: 'and',
    subfilters,
  };
};
